<script lang="ts" setup>
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useRouter } from 'vue-router'
  import { useFilesStore } from '@clevver/stores/files.store'
  import FacetChip from '@clevver/components/dashboard/facet-chip.vue'
  import { formatBytes, percentOf } from '@clevver/helpers/files'

  const filesStore = useFilesStore()
  const router = useRouter()
  const { tags, quota } = storeToRefs(filesStore)

  const usedPercent = computed(() => percentOf(quota.value?.used ?? 0, quota.value?.total ?? 0))

  function toggleTag(tag: string) {
    if (filesStore.selectedTags.includes(tag)) filesStore.deselectTag(tag)
    else filesStore.selectTag(tag)
    // The dashboard view watches route.query and refreshes the file list.
    router.push({ query: filesStore.queryFromFilter })
  }
  function clearTags() {
    filesStore.resetTags(false)
    router.push({ query: {} })
  }
  const tagSelected = (tag: string) => filesStore.selectedTags.includes(tag)
  const countForTag = (tag: string) => filesStore.tagCounts?.[tag] || ''
</script>

<template>
  <div class="rail">
    <div class="rail__section">
      <div class="rail__label">
        <span>Quota</span>
        <span v-if="quota && quota.total" class="t-mono rail__quota">{{ formatBytes(quota.used) }} / {{ formatBytes(quota.total) }}</span>
      </div>
      <div v-if="quota && quota.total" class="rail__bar" role="progressbar"
           :aria-valuenow="usedPercent" aria-valuemin="0" aria-valuemax="100">
        <div class="rail__bar-fill" :style="{ width: usedPercent + '%' }" />
      </div>
      <div v-else class="rail__note">You don't have an active Clevver license, or it has expired.</div>
    </div>

    <div class="rail__section">
      <div class="rail__label">
        <span>Tags</span>
        <button v-if="filesStore.selectedTags.length" type="button" class="rail__clear" @click="clearTags">clear</button>
      </div>
      <div class="rail__chips">
        <FacetChip v-for="tag in tags" :key="tag" mono :active="tagSelected(tag)" @click="toggleTag(tag)">
          #{{ tag }}<span class="rail__count">{{ countForTag(tag) }}</span>
        </FacetChip>
        <span v-if="!tags.length" class="rail__note">No tags yet.</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rail {
  display: flex; flex-direction: column; gap: 26px; padding: 22px 20px;
  &__section { display: flex; flex-direction: column; }
  &__label {
    font-size: 11px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase;
    color: var(--c-faint); display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 10px;
  }
  &__quota { font-size: 11px; letter-spacing: 0; text-transform: none; color: var(--c-muted); }
  &__clear { border: none; background: none; cursor: pointer; font-size: 11px; color: var(--c-accent); }
  &__bar { height: 8px; border-radius: var(--radius-bar); overflow: hidden; background: var(--c-surface-2); }
  &__bar-fill { height: 100%; background: var(--c-accent); border-radius: var(--radius-bar); transition: width 0.3s ease; }
  &__note { font-size: 12px; color: var(--c-faint); }
  &__chips { display: flex; flex-wrap: wrap; gap: 6px; }
  &__count { color: var(--c-faint); margin-left: 2px; }
}
</style>
